export type CrimeCategory = 'street' | 'brawl' | 'sneak' | 'sea' | 'heist';
export type CaptureDestination = 'jail' | 'hospital' | 'split';

export interface CrimeLootEntry {
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  chance: number;
}

export interface Crime {
  id: string;
  name: string;
  category: CrimeCategory;
  tier: number;
  nerveCost: number;
  levelRequired: number;
  crimeSkillRequired: number;
  baseOutcomes: { success: number; unsuccessful: number; failure: number; captured: number };
  goldMin: number;
  goldMax: number;
  xpReward: number;
  lootTable: CrimeLootEntry[];
  failureHpLossMin: number;
  failureHpLossMax: number;
  captureDestination: CaptureDestination;
  captureDurationMin: number;
  captureDurationMax: number;
  flavor: string;
  successText: string;
  unsuccessfulText: string;
  failureText: string;
  capturedText: string;
  cooldownDays?: number;
  refreshCostMarks?: number;
  requiresShip?: boolean;
  requiredShipClass?: 'sloop' | 'brigantine' | 'frigate' | 'galleon';
  crossSkillRequirements?: { crimeSkill?: number; seaCrimeSkill?: number; seamanship?: number };
  requiredLocation?: string;
}

export const CRIME_SKILL_FOR_TIER: Record<number, number> = {
  1: 1,
  2: 1,
  3: 10,
  4: 20,
  5: 30,
  6: 45,
  7: 60,
  8: 75,
  9: 85,
  10: 95,
};

export const CRIMES: Crime[] = [
  {
    id: 'pickpocket_drunk',
    name: 'Pickpocket a Drunk',
    category: 'street',
    tier: 1,
    nerveCost: 2,
    levelRequired: 1,
    crimeSkillRequired: 1,
    baseOutcomes: { success: 0.6, unsuccessful: 0.25, failure: 0.1, captured: 0.05 },
    goldMin: 8,
    goldMax: 25,
    xpReward: 20,
    lootTable: [{ rarity: 'common', chance: 0.05 }],
    failureHpLossMin: 0,
    failureHpLossMax: 0,
    captureDestination: 'split',
    captureDurationMin: 5,
    captureDurationMax: 5,
    flavor:
      "An old man stumbles out of the Sea Dog Tavern, smelling of bad rum and worse luck. His purse is on his hip. He won't remember you in the morning.",
    successText:
      'Your fingers find the purse strings. He sways into the doorframe as you walk past, already forgotten.',
    unsuccessfulText:
      'Another drunk grabs him for balance before you can close the distance. Both stumble off together into the dark.',
    failureText:
      'He spins clumsily and his elbow catches your ribs. Drunks remember faces sometimes. You bolt before he places yours.',
    capturedText:
      "A redcoat steps from the shadow of the tavern eaves. 'Easy now, lad. Keep those hands where I can see them.'",
  },
  {
    id: 'snatch_coin_purse',
    name: 'Snatch a Market Coin Purse',
    category: 'street',
    tier: 1,
    nerveCost: 2,
    levelRequired: 1,
    crimeSkillRequired: 1,
    baseOutcomes: { success: 0.55, unsuccessful: 0.25, failure: 0.12, captured: 0.08 },
    goldMin: 10,
    goldMax: 30,
    xpReward: 22,
    lootTable: [{ rarity: 'common', chance: 0.03 }],
    failureHpLossMin: 0,
    failureHpLossMax: 0,
    captureDestination: 'jail',
    captureDurationMin: 5,
    captureDurationMax: 5,
    flavor:
      "Market day. The merchant's wife is haggling over silk. Her coin purse hangs from her belt, fat with the morning's takings. A quick hand. A faster exit.",
    successText:
      "You brush past as a wagon clatters by. The purse comes free in your palm. He keeps counting what's left.",
    unsuccessfulText:
      "A buyer pulls him aside before you can move. By the time he's free, his hand is on the purse.",
    failureText:
      "Your hand finds the purse but his finds your wrist. The fishmonger's wife screams thief and you run, his cudgel meeting your back twice before you reach the alleys.",
    capturedText:
      "The merchant doesn't fight. He just shouts. By the time you've gone twenty paces, three constables have you cornered between a wagon and a wall.",
  },
  {
    id: 'roll_sleeping_sailor',
    name: 'Roll a Sleeping Sailor',
    category: 'street',
    tier: 1,
    nerveCost: 3,
    levelRequired: 1,
    crimeSkillRequired: 1,
    baseOutcomes: { success: 0.5, unsuccessful: 0.3, failure: 0.15, captured: 0.05 },
    goldMin: 15,
    goldMax: 40,
    xpReward: 25,
    lootTable: [{ rarity: 'common', chance: 0.08 }],
    failureHpLossMin: 0,
    failureHpLossMax: 0,
    captureDestination: 'hospital',
    captureDurationMin: 5,
    captureDurationMax: 10,
    flavor:
      'A sailor is passed out in an alley behind the Lazy Goose. Pockets stuffed with shore leave pay. The night is dark, the streets are quiet. Just go through them quickly.',
    successText: "His belt is loose. The pouch slides free without a sound. He doesn't stir.",
    unsuccessfulText:
      "He rolls over before you've reached him, mumbling something incoherent. Better not to risk it now.",
    failureText:
      'Your knee creaks as you crouch. He wakes mid-grab and his fist finds your jaw before you can stand.',
    capturedText:
      "He doesn't wake quietly. He wakes ROARING, and his crewmates spill from the doorway across the alley. You're carried half-conscious back to wherever it is they take pirates here.",
  },
  {
    id: 'fish_market_theft',
    name: 'Stealing from the Fish Market',
    category: 'street',
    tier: 2,
    nerveCost: 3,
    levelRequired: 2,
    crimeSkillRequired: 1,
    baseOutcomes: { success: 0.5, unsuccessful: 0.28, failure: 0.17, captured: 0.05 },
    goldMin: 20,
    goldMax: 60,
    xpReward: 35,
    lootTable: [
      { rarity: 'common', chance: 0.1 },
      { rarity: 'uncommon', chance: 0.02 },
    ],
    failureHpLossMin: 0,
    failureHpLossMax: 0,
    captureDestination: 'jail',
    captureDurationMin: 10,
    captureDurationMax: 10,
    flavor:
      "Old Tom Buford has been running this fish stall for thirty years. He's slow, half-blind, and counts his coins after every sale. The trick is to keep him talking while your hands work.",
    successText:
      'Three good fish slip into your coat. The smell will haunt your shirt for days but the meat will sell well in the slums tonight.',
    unsuccessfulText:
      'His wife rounds the corner with a basket and your moment is gone. She has the eyes of a hawk.',
    failureText:
      'You misjudge your reach and the ice clatters. The fishmonger turns just in time to swing his gutting knife. It catches your hand before you bolt.',
    capturedText:
      "The fishmonger's son has been watching you the whole time. He grabs you from behind as his father raises the alarm. By the time you stop struggling, the constables are already running.",
  },
  {
    id: 'short_con',
    name: 'Run a Short Con',
    category: 'street',
    tier: 2,
    nerveCost: 4,
    levelRequired: 3,
    crimeSkillRequired: 1,
    baseOutcomes: { success: 0.45, unsuccessful: 0.3, failure: 0.18, captured: 0.07 },
    goldMin: 30,
    goldMax: 80,
    xpReward: 40,
    lootTable: [{ rarity: 'uncommon', chance: 0.05 }],
    failureHpLossMin: 0,
    failureHpLossMax: 0,
    captureDestination: 'jail',
    captureDurationMin: 10,
    captureDurationMax: 15,
    flavor:
      'Find a mark with more coin than wit. Tell him a story he wants to believe. Take what he gives freely. The best cons leave them feeling like winners.',
    successText:
      'He presses two crowns into your hand and wishes you well. The tears were a particularly nice touch, you think.',
    unsuccessfulText:
      "He's wiser than he looks. He shakes his head, pats your shoulder, and moves on without parting with a single coin.",
    failureText:
      'Halfway through your tale, you recognize him from the dock yesterday. He recognizes you too. The chase that follows ends with you bleeding behind a chandlery.',
    capturedText:
      "He listens patiently to your whole story. Then, when you've finished, he whistles. Two of his men step out from a doorway you hadn't noticed. He's not a fresh arrival at all.",
  },
  {
    id: 'burglarize_cottage',
    name: 'Burglarize a Cottage',
    category: 'street',
    tier: 3,
    nerveCost: 5,
    levelRequired: 5,
    crimeSkillRequired: 10,
    baseOutcomes: { success: 0.55, unsuccessful: 0.25, failure: 0.15, captured: 0.05 },
    goldMin: 50,
    goldMax: 150,
    xpReward: 60,
    lootTable: [
      { rarity: 'common', chance: 0.2 },
      { rarity: 'uncommon', chance: 0.05 },
    ],
    failureHpLossMin: 0,
    failureHpLossMax: 0,
    captureDestination: 'split',
    captureDurationMin: 10,
    captureDurationMax: 20,
    flavor:
      "A merchant's cottage at the edge of town. The husband is at sea. The wife is at market. There's a window unlatched on the second floor. No time like the present.",
    successText:
      "The lock yields to a length of wire. Inside: silver candlesticks, a small chest of coins, and a fine wool coat. You're gone in under a minute.",
    unsuccessfulText:
      "A neighbor's dog starts barking at the wrong moment. You abandon the cottage and slip back into the streets before anyone investigates.",
    failureText:
      "The lock holds. As you work it, the owner returns home early. He's a big man and the door is the only exit. The window costs you blood but not your freedom.",
    capturedText:
      "The owner hasn't gone anywhere — he was upstairs sleeping. He comes down with a musket and shouts for help. You make it three streets before the constables overtake you.",
  },
  {
    id: 'counterfeit_coin',
    name: 'Counterfeit Coin Pass',
    category: 'street',
    tier: 3,
    nerveCost: 6,
    levelRequired: 7,
    crimeSkillRequired: 10,
    baseOutcomes: { success: 0.5, unsuccessful: 0.3, failure: 0.15, captured: 0.05 },
    goldMin: 80,
    goldMax: 200,
    xpReward: 75,
    lootTable: [{ rarity: 'uncommon', chance: 0.15 }],
    failureHpLossMin: 0,
    failureHpLossMax: 0,
    captureDestination: 'jail',
    captureDurationMin: 20,
    captureDurationMax: 30,
    flavor:
      'Pass off polished bronze as Spanish doubloons. Works only on the gullible or the desperate. A good fake will fool a tavern keeper but not a banker. Choose your marks wisely.',
    successText:
      "The jeweler bites into one coin, holds another to the light, and shrugs. Your change is real silver. The fakes are now somebody else's problem.",
    unsuccessfulText:
      "He's distracted with another customer when you reach the counter. By the time he's free, you've thought better of it and pocketed the fakes.",
    failureText:
      "The jeweler's eyes narrow at the second coin. He doesn't shout, but his apprentice slips out the back door. You run before they return with friends.",
    capturedText:
      'He smiles, nods, takes your fake coins, and asks you to wait while he fetches change. He returns with two armed men and a smile that has not changed at all.',
  },
  {
    id: 'tavern_brawl',
    name: 'Tavern Brawl',
    category: 'brawl',
    tier: 2,
    nerveCost: 4,
    levelRequired: 2,
    crimeSkillRequired: 1,
    baseOutcomes: { success: 0.5, unsuccessful: 0.25, failure: 0.2, captured: 0.05 },
    goldMin: 25,
    goldMax: 70,
    xpReward: 35,
    lootTable: [{ rarity: 'common', chance: 0.08 }],
    failureHpLossMin: 15,
    failureHpLossMax: 35,
    captureDestination: 'hospital',
    captureDurationMin: 10,
    captureDurationMax: 10,
    flavor:
      "A drunk insults you. The crowd is watching. You can let it slide and lose face, or you can break his jaw and take his purse. The barkeep won't intervene as long as the furniture survives.",
    successText:
      'Chairs fly. Bottles break. In the chaos, you relieve the well-dressed gentleman of his purse, his ring, and a fine flask of brandy.',
    unsuccessfulText:
      'The fight breaks up before it really starts. The barkeep is well-armed and his glare clears the room. Nothing for you tonight.',
    failureText:
      'You misjudge the swing. A bottle catches your temple and the world turns soft and slow. You wake up an hour later in the alley, your own purse missing.',
    capturedText:
      "The well-dressed gentleman isn't unarmed and isn't alone. His bodyguards have your arms pinned before the brawl is fully underway. You're outside in the constables' hands before you can blink.",
  },
  {
    id: 'dockside_beatdown',
    name: 'Dockside Beatdown',
    category: 'brawl',
    tier: 3,
    nerveCost: 5,
    levelRequired: 5,
    crimeSkillRequired: 10,
    baseOutcomes: { success: 0.55, unsuccessful: 0.25, failure: 0.15, captured: 0.05 },
    goldMin: 40,
    goldMax: 120,
    xpReward: 50,
    lootTable: [
      { rarity: 'common', chance: 0.12 },
      { rarity: 'uncommon', chance: 0.03 },
    ],
    failureHpLossMin: 20,
    failureHpLossMax: 40,
    captureDestination: 'hospital',
    captureDurationMin: 15,
    captureDurationMax: 15,
    flavor:
      'A rival pirate has been talking ill of your captain. The docks are quiet at this hour. Words are nothing. Bruises are a lesson. Broken ribs are a statement.',
    successText:
      "Two punches and he's against a piling, the wind out of him. His purse and the silver buckle from his coat are yours before he can call for help.",
    unsuccessfulText:
      "He's not alone after all — his first mate appears around a stack of crates carrying a lantern. You melt back into the shadows. Not tonight.",
    failureText:
      "He's quicker than he looks. His first punch takes you in the throat and the second drops you against the cold stone of the quay. You crawl away with nothing.",
    capturedText:
      "He doesn't fight. He just holds on, shouting, while his men come running. You're knee-deep in seawater and half-strangled by the time the harbormaster's men arrive.",
  },
  {
    id: 'bouncers_tribute',
    name: "Bouncer's Tribute",
    category: 'brawl',
    tier: 3,
    nerveCost: 6,
    levelRequired: 7,
    crimeSkillRequired: 10,
    baseOutcomes: { success: 0.5, unsuccessful: 0.3, failure: 0.15, captured: 0.05 },
    goldMin: 60,
    goldMax: 180,
    xpReward: 65,
    lootTable: [{ rarity: 'uncommon', chance: 0.15 }],
    failureHpLossMin: 25,
    failureHpLossMax: 45,
    captureDestination: 'hospital',
    captureDurationMin: 20,
    captureDurationMax: 20,
    flavor:
      "The bouncer at the Sailor's Rest takes a cut of every transaction in the place. He's big, he's mean, and he's got friends. Convince him you're worth his protection.",
    successText:
      'He never sees you. The flowerpot lifts cleanly and the coins inside are real gold.',
    unsuccessfulText:
      "Two patrons stagger into the alley to relieve themselves. By the time they're gone, the bouncer is collecting his own stash. Not tonight.",
    failureText:
      "He hears you over the music spilling from the door. His club catches your shin before you can run. You're limping for the next week.",
    capturedText:
      "The bouncer was waiting in the dark. He grabs your collar and lifts you off the ground without effort. 'Wrong flowerpot, friend,' he says.",
  },
  {
    id: 'slip_storehouse',
    name: 'Slip Into a Storehouse',
    category: 'sneak',
    tier: 3,
    nerveCost: 6,
    levelRequired: 5,
    crimeSkillRequired: 10,
    baseOutcomes: { success: 0.45, unsuccessful: 0.3, failure: 0.2, captured: 0.05 },
    goldMin: 50,
    goldMax: 150,
    xpReward: 65,
    lootTable: [
      { rarity: 'common', chance: 0.25 },
      { rarity: 'uncommon', chance: 0.1 },
    ],
    failureHpLossMin: 15,
    failureHpLossMax: 30,
    captureDestination: 'jail',
    captureDurationMin: 20,
    captureDurationMax: 20,
    flavor:
      'The storehouse on the back lane keeps imported goods for a Spanish trader. The locks are good. The walls are not. A skilled thief finds the gaps in any fortress.',
    successText:
      "The window pries open without a sound. Inside, you fill a sack with twelve pounds of pure silver. You're back in the street before the watchman returns.",
    unsuccessfulText:
      "The watchman's schedule has changed. He passes within feet of you as you crouch behind the storehouse. You retreat without trying the window.",
    failureText:
      'The window opens but a board within is rotten. You crash through and twist your ankle. The watchman finds you trying to crawl out.',
    capturedText:
      "The watchman wasn't on his usual route — he was waiting just inside the door. He clubs you across the jaw before you've fully entered the building.",
  },

  // ===================================================================
  // STREET CRIME — Tier 4
  // ===================================================================
  {
    id: 'highway_robbery',
    name: 'Highway Robbery on the Coastal Road',
    category: 'street',
    tier: 4,
    nerveCost: 8,
    levelRequired: 10,
    crimeSkillRequired: 20,
    baseOutcomes: { success: 0.45, unsuccessful: 0.3, failure: 0.18, captured: 0.07 },
    goldMin: 150,
    goldMax: 400,
    xpReward: 100,
    lootTable: [
      { rarity: 'uncommon', chance: 0.2 },
      { rarity: 'rare', chance: 0.05 },
    ],
    failureHpLossMin: 10,
    failureHpLossMax: 25,
    captureDestination: 'jail',
    captureDurationMin: 30,
    captureDurationMax: 30,
    flavor:
      "Hide behind the rocks where the coastal road bends. Wait for the merchant's carriage. Step out with sword drawn and demand the coin. Most surrender. Some don't.",
    successText:
      'The driver pulls the reins. The merchant raises his hands before you finish speaking. His strongbox yields without argument.',
    unsuccessfulText:
      'A naval patrol crests the ridge ahead. You step back into the scrub and let the carriage pass unchallenged.',
    failureText:
      "The merchant's guard fires from the carriage window. The ball grazes your shoulder and you dive into the ditch, bleeding and empty-handed.",
    capturedText:
      'The carriage was bait. Redcoats pour from the treeline on both sides. Your sword is in the dirt before you can draw breath.',
  },

  // ===================================================================
  // BRAWL CRIMES — Tier 4-9
  // ===================================================================
  {
    id: 'smash_and_grab',
    name: 'Smash and Grab',
    category: 'brawl',
    tier: 4,
    nerveCost: 7,
    levelRequired: 10,
    crimeSkillRequired: 20,
    baseOutcomes: { success: 0.5, unsuccessful: 0.28, failure: 0.17, captured: 0.05 },
    goldMin: 100,
    goldMax: 300,
    xpReward: 85,
    lootTable: [
      { rarity: 'uncommon', chance: 0.2 },
      { rarity: 'rare', chance: 0.05 },
    ],
    failureHpLossMin: 30,
    failureHpLossMax: 50,
    captureDestination: 'jail',
    captureDurationMin: 25,
    captureDurationMax: 30,
    flavor:
      'Break the shop window with a brick. Vault the counter. Grab what you can. Run. The shopkeep is fat and slow. The constables are two streets away. You have maybe forty seconds.',
    successText:
      'Glass shatters. Your boots hit the counter. Silver candlesticks, a pocket watch, a fistful of coins. You vault back out before the echo fades.',
    unsuccessfulText:
      'The shop is dark and the door barred from inside. The shopkeep sleeps upstairs tonight. You circle the building twice and leave it standing.',
    failureText:
      'The shopkeep was behind the counter with a blunderbuss. The blast takes a chunk from the doorframe and peppers your arm with splinters. You run bleeding.',
    capturedText:
      'You clear the counter but the back door opens onto two watchmen sharing a pipe. They have you by the collar before the glass stops falling.',
  },
  {
    id: 'pit_fighting',
    name: 'Pit Fighting',
    category: 'brawl',
    tier: 5,
    nerveCost: 10,
    levelRequired: 15,
    crimeSkillRequired: 30,
    baseOutcomes: { success: 0.45, unsuccessful: 0.3, failure: 0.2, captured: 0.05 },
    goldMin: 200,
    goldMax: 600,
    xpReward: 130,
    lootTable: [{ rarity: 'rare', chance: 0.25 }],
    failureHpLossMin: 40,
    failureHpLossMax: 70,
    captureDestination: 'hospital',
    captureDurationMin: 30,
    captureDurationMax: 30,
    requiredLocation: 'tortuga',
    flavor:
      'The Fight Pit at Tortuga is just a sandy basement with a circle drawn in chalk. Two men enter. One walks out. The crowd cheers either way. The purse goes to the survivor.',
    successText:
      'Three rounds. His nose breaks in the second. He drops in the third. The crowd throws coins and you scoop them from the sand before they stop rolling.',
    unsuccessfulText:
      'The pit boss calls it a draw when neither of you can stand straight. The crowd boos. No purse tonight.',
    failureText:
      "He's faster than he looks. A knee to your ribs, an elbow to your temple. You taste sand and copper. Someone drags you out by your ankles.",
    capturedText:
      "You go down hard in the first exchange. The crowd surges forward. When you wake, you're in a cot that smells of rum and old blood. The doctor says two ribs.",
  },
  {
    id: 'intimidate_merchant',
    name: 'Intimidate a Merchant',
    category: 'brawl',
    tier: 5,
    nerveCost: 8,
    levelRequired: 17,
    crimeSkillRequired: 30,
    baseOutcomes: { success: 0.5, unsuccessful: 0.3, failure: 0.15, captured: 0.05 },
    goldMin: 250,
    goldMax: 700,
    xpReward: 120,
    lootTable: [
      { rarity: 'rare', chance: 0.2 },
      { rarity: 'epic', chance: 0.05 },
    ],
    failureHpLossMin: 20,
    failureHpLossMax: 40,
    captureDestination: 'jail',
    captureDurationMin: 30,
    captureDurationMax: 30,
    flavor:
      "The merchant's been paying a rival crew for protection. Convince him to switch sides. Words are cheap. A demonstration of broken furniture is cheaper. Hands stay clean if you do it right.",
    successText:
      'One chair through his front window and he reaches for his strongbox. The new arrangement begins today.',
    unsuccessfulText:
      'His rival crew is drinking in the room next door. You hear their voices through the wall and excuse yourself politely.',
    failureText:
      "He's not as frightened as he should be. His hired muscle steps from behind the curtain and the first blow catches your kidney.",
    capturedText:
      'He was expecting you. The constables were in the back room the whole time, waiting for you to make your threat on record.',
  },
  {
    id: 'bare_knuckle_champion',
    name: 'Bare-Knuckle Champion',
    category: 'brawl',
    tier: 6,
    nerveCost: 12,
    levelRequired: 25,
    crimeSkillRequired: 45,
    baseOutcomes: { success: 0.5, unsuccessful: 0.25, failure: 0.2, captured: 0.05 },
    goldMin: 500,
    goldMax: 1500,
    xpReward: 250,
    lootTable: [
      { rarity: 'rare', chance: 0.3 },
      { rarity: 'epic', chance: 0.1 },
    ],
    failureHpLossMin: 60,
    failureHpLossMax: 90,
    captureDestination: 'hospital',
    captureDurationMin: 45,
    captureDurationMax: 60,
    cooldownDays: 0.25,
    refreshCostMarks: 30,
    flavor:
      "The Champion of the Reef has held the title for two years. They say his fists shatter teeth. He's bigger than you. He's slower than you. The crowd doesn't care which of you wins, only that one of you doesn't get up.",
    successText:
      'Six rounds of brutal, silent work. His left eye swells shut in the fourth. He goes down in the sixth and stays down. The purse is heavy. The crowd remembers your name.',
    unsuccessfulText:
      'The champion is called away on urgent business. A ship docking, they say. The crowd disperses grumbling. No fight tonight.',
    failureText:
      'His right cross puts stars in your vision. His follow-up puts you on your back. You hear the crowd counting before the world goes dark.',
    capturedText:
      "You go down in the second round. Something cracks in your jaw. The pit doctor shakes his head and they carry you to the surgeon's table.",
  },
  {
    id: 'captains_duel',
    name: "Captain's Duel",
    category: 'brawl',
    tier: 7,
    nerveCost: 12,
    levelRequired: 35,
    crimeSkillRequired: 60,
    baseOutcomes: { success: 0.45, unsuccessful: 0.3, failure: 0.2, captured: 0.05 },
    goldMin: 800,
    goldMax: 2500,
    xpReward: 400,
    lootTable: [
      { rarity: 'epic', chance: 0.3 },
      { rarity: 'legendary', chance: 0.08 },
    ],
    failureHpLossMin: 70,
    failureHpLossMax: 100,
    captureDestination: 'hospital',
    captureDurationMin: 60,
    captureDurationMax: 60,
    cooldownDays: 0.25,
    refreshCostMarks: 30,
    flavor:
      "A rival captain has called you out by name in three ports. Word travels. If you don't answer, your reputation suffers. If you answer and lose, your reputation suffers worse. Better to win.",
    successText:
      'Steel rings in the morning air. His cutlass flies from his hand on the third exchange. He yields. His purse, his reputation, and his best coat are yours.',
    unsuccessfulText:
      'He sends word through a boy: the duel is postponed. His ship sails on the evening tide. Another time, he says.',
    failureText:
      'His blade finds the gap between your ribs. Not deep enough to kill but deep enough to end the fight. You limp away with nothing but a new scar.',
    capturedText:
      'He runs you through the forearm and pins you to a piling. His seconds hold you there while the surgeon comes. The duel is over. You lost.',
  },
  {
    id: 'tournament_of_steel',
    name: 'Tournament of Steel',
    category: 'brawl',
    tier: 8,
    nerveCost: 14,
    levelRequired: 50,
    crimeSkillRequired: 75,
    baseOutcomes: { success: 0.5, unsuccessful: 0.25, failure: 0.22, captured: 0.03 },
    goldMin: 1500,
    goldMax: 5000,
    xpReward: 700,
    lootTable: [
      { rarity: 'epic', chance: 0.4 },
      { rarity: 'legendary', chance: 0.15 },
    ],
    failureHpLossMin: 80,
    failureHpLossMax: 150,
    captureDestination: 'hospital',
    captureDurationMin: 90,
    captureDurationMax: 90,
    cooldownDays: 1,
    refreshCostMarks: 100,
    flavor:
      "Once a year, the Tortuga elite host a tournament of single combat. The winner takes home a purse, the loser's gear, and bragging rights for twelve months. The brackets are brutal. Twelve fighters. One winner.",
    successText:
      'Four bouts. Four men on the ground. The final opponent goes down with a broken collarbone and the tournament purse is placed in your hands. Tortuga knows your name now.',
    unsuccessfulText:
      'Rain floods the arena. The tournament is delayed. The crowd drifts toward the taverns, and so do you.',
    failureText:
      'The semi-final opponent fights dirty. A hidden blade opens your thigh and you go down on one knee. The tournament doctor pulls you from the bracket.',
    capturedText:
      "The final bout ends with your head bouncing off the cobblestones. You wake in the tournament infirmary missing a tooth and whatever dignity you'd carried in.",
  },
  {
    id: 'champions_test',
    name: "The Champion's Test",
    category: 'brawl',
    tier: 9,
    nerveCost: 16,
    levelRequired: 75,
    crimeSkillRequired: 85,
    baseOutcomes: { success: 0.5, unsuccessful: 0.25, failure: 0.23, captured: 0.02 },
    goldMin: 5000,
    goldMax: 15000,
    xpReward: 1500,
    lootTable: [
      { rarity: 'epic', chance: 0.5 },
      { rarity: 'legendary', chance: 0.25 },
    ],
    failureHpLossMin: 100,
    failureHpLossMax: 180,
    captureDestination: 'hospital',
    captureDurationMin: 120,
    captureDurationMax: 180,
    cooldownDays: 1,
    refreshCostMarks: 100,
    flavor:
      "The Champion holds court in a converted warehouse on Maracaibo's docks. He doesn't fight for purses. He fights to prove. Beat him and you join an unwritten list of names whispered in taverns. Lose and you become a story he tells over rum.",
    successText:
      'Eleven minutes of silence broken only by steel and breathing. He goes down on one knee and looks up with something like respect. The warehouse erupts. Your name joins the list.',
    unsuccessfulText:
      "The Champion is at sea. His second says he'll return in a fortnight. The warehouse stands empty, the chalk circle undisturbed.",
    failureText:
      "He moves like water around a stone. His blade opens your guard, your shoulder, and your pride in three fluid motions. You're carried out on a stretcher.",
    capturedText:
      "He doesn't just beat you. He dismantles you. Methodically. Precisely. Each strike placed where the surgeon will have the most work. A lesson in the cost of ambition.",
  },

  // ===================================================================
  // SNEAK CRIMES — Tier 4-10
  // ===================================================================
  {
    id: 'sneak_aboard_ship',
    name: 'Sneak Aboard a Docked Ship',
    category: 'sneak',
    tier: 4,
    nerveCost: 8,
    levelRequired: 10,
    crimeSkillRequired: 20,
    baseOutcomes: { success: 0.45, unsuccessful: 0.28, failure: 0.22, captured: 0.05 },
    goldMin: 100,
    goldMax: 300,
    xpReward: 95,
    lootTable: [
      { rarity: 'uncommon', chance: 0.2 },
      { rarity: 'rare', chance: 0.08 },
    ],
    failureHpLossMin: 20,
    failureHpLossMax: 40,
    captureDestination: 'jail',
    captureDurationMin: 25,
    captureDurationMax: 25,
    flavor:
      "A merchant ship sits at anchor, half-loaded with goods. The crew is ashore, drinking their pay. The watch is one bored deckhand who can't tell the difference between a friend and a shadow at this hour.",
    successText:
      "Below decks, the captain's chest yields easily. Navigation instruments, a pouch of Spanish reales, and a silver-handled letter opener. You're back on the quay before the watch completes his circuit.",
    unsuccessfulText:
      'The gangplank creaks under a gust of wind and the watchman straightens. You freeze against the piling until he settles, then withdraw.',
    failureText:
      'The deckhand is more alert than he looks. A belaying pin catches your temple as you reach the main hatch. You roll overboard and swim for the far dock, bleeding into the harbor.',
    capturedText:
      "The crew wasn't ashore. They were in the hold, armed and waiting. The captain has been expecting thieves all week.",
  },
  {
    id: 'lockbreaker',
    name: 'Lockbreaker',
    category: 'sneak',
    tier: 4,
    nerveCost: 7,
    levelRequired: 12,
    crimeSkillRequired: 20,
    baseOutcomes: { success: 0.55, unsuccessful: 0.25, failure: 0.18, captured: 0.02 },
    goldMin: 120,
    goldMax: 350,
    xpReward: 85,
    lootTable: [
      { rarity: 'uncommon', chance: 0.3 },
      { rarity: 'rare', chance: 0.05 },
    ],
    failureHpLossMin: 0,
    failureHpLossMax: 0,
    captureDestination: 'jail',
    captureDurationMin: 20,
    captureDurationMax: 20,
    flavor:
      "Pick a lock that wasn't meant to be picked. Anything from a private chest to a counting house safe. The skill is in the patience, not the speed. Hurry, and you'll snap the picks. Or worse.",
    successText:
      'The tumblers yield one by one. The lid opens to reveal neatly stacked coin and a gemstone brooch wrapped in velvet. You close it again, minus the contents.',
    unsuccessfulText:
      "The lock is finer than expected — German engineering, triple-ward. Your picks aren't up to the job. You withdraw them intact and walk away.",
    failureText:
      'The last pick snaps inside the mechanism. The lock seizes permanently. You leave with nothing but the knowledge that the locksmith will find your broken tool inside.',
    capturedText:
      "The lock opens — onto a room where the owner is sitting upright in his chair, watching the door. 'I wondered when you'd come,' he says, as his man steps behind you.",
  },
  {
    id: 'steal_cargo_manifest',
    name: 'Steal a Cargo Manifest',
    category: 'sneak',
    tier: 5,
    nerveCost: 9,
    levelRequired: 15,
    crimeSkillRequired: 30,
    baseOutcomes: { success: 0.45, unsuccessful: 0.3, failure: 0.2, captured: 0.05 },
    goldMin: 200,
    goldMax: 500,
    xpReward: 130,
    lootTable: [
      { rarity: 'uncommon', chance: 0.25 },
      { rarity: 'rare', chance: 0.1 },
    ],
    failureHpLossMin: 15,
    failureHpLossMax: 30,
    captureDestination: 'jail',
    captureDurationMin: 30,
    captureDurationMax: 30,
    flavor:
      "The Spanish governor's office keeps detailed cargo records. Who's shipping what, where, when. Information like this sells for more than the goods themselves. Get in, find the right document, get out.",
    successText:
      'Third drawer, second shelf. The manifest lists twelve ships, their cargo, and their departure dates. Three different captains will pay handsomely for this.',
    unsuccessfulText:
      'The office is locked and the windows barred with new iron. Someone has upgraded the security since your last visit. Not tonight.',
    failureText:
      'You find the manifest but knock a bottle of ink from the desk. The crash brings the night clerk running. You escape through the window, cutting your palm on the latch.',
    capturedText:
      "The governor's personal guard finds you elbow-deep in the filing cabinet. He doesn't speak. He just draws his pistol and waits for you to step away from the desk.",
  },
  {
    id: 'master_thievery',
    name: 'Master Thievery',
    category: 'sneak',
    tier: 6,
    nerveCost: 11,
    levelRequired: 25,
    crimeSkillRequired: 45,
    baseOutcomes: { success: 0.5, unsuccessful: 0.25, failure: 0.2, captured: 0.05 },
    goldMin: 400,
    goldMax: 1200,
    xpReward: 230,
    lootTable: [
      { rarity: 'rare', chance: 0.3 },
      { rarity: 'epic', chance: 0.08 },
    ],
    failureHpLossMin: 30,
    failureHpLossMax: 50,
    captureDestination: 'jail',
    captureDurationMin: 45,
    captureDurationMax: 45,
    flavor:
      'A wealthy merchant keeps his real fortune in a hidden compartment behind his counting room. The compartment opens with a clockwork mechanism. The mechanism makes noise. You have one chance.',
    successText:
      'The clockwork turns without protest. Behind the panel: gold bars, a stack of bearer notes, and a ruby the size of your thumbnail. You take everything that fits in your coat.',
    unsuccessfulText:
      'The counting room is occupied — two clerks working late by candlelight, hunched over ledgers. You watch from the alley until dawn, then give it up.',
    failureText:
      "The mechanism jams. You force it. Something inside breaks with a sound like a gunshot. The merchant's dogs start barking and you flee through the garden, tearing your coat on the iron fence.",
    capturedText:
      'The hidden compartment was a trap. The real vault is elsewhere. The constables who emerge from the back room have been waiting all evening.',
  },
  {
    id: 'spy_naval_patrol',
    name: 'Spy on a Naval Patrol',
    category: 'sneak',
    tier: 6,
    nerveCost: 12,
    levelRequired: 30,
    crimeSkillRequired: 45,
    baseOutcomes: { success: 0.55, unsuccessful: 0.25, failure: 0.17, captured: 0.03 },
    goldMin: 350,
    goldMax: 1000,
    xpReward: 250,
    lootTable: [
      { rarity: 'rare', chance: 0.25 },
      { rarity: 'epic', chance: 0.1 },
      { rarity: 'legendary', chance: 0.02 },
    ],
    failureHpLossMin: 40,
    failureHpLossMax: 60,
    captureDestination: 'hospital',
    captureDurationMin: 60,
    captureDurationMax: 60,
    flavor:
      'Track a Royal Marine patrol through the alleys. Listen at windows. Note their routes, their timing, their leaders. Information that lets honest captains avoid trouble. Or dishonest ones plan ambushes.',
    successText:
      'Three hours on rooftops yields the full rotation schedule, the names of the officers, and the location of their armory. Captains will bid for this intelligence.',
    unsuccessfulText:
      'The patrol changes its route mid-evening. You lose them in the maze of alleys near the fort and return with nothing worth selling.',
    failureText:
      'A marine spots your silhouette on the rooftop. The musket ball chips the chimney by your ear. You slide down the far side of the building and land badly on your ankle.',
    capturedText:
      "Two marines were posted on the roof you chose for observation. The Navy doesn't take kindly to spies. The beating is professional and thorough.",
  },
  {
    id: 'vault_heist',
    name: 'Vault Heist',
    category: 'sneak',
    tier: 7,
    nerveCost: 13,
    levelRequired: 40,
    crimeSkillRequired: 60,
    baseOutcomes: { success: 0.45, unsuccessful: 0.28, failure: 0.22, captured: 0.05 },
    goldMin: 1000,
    goldMax: 3000,
    xpReward: 500,
    lootTable: [
      { rarity: 'epic', chance: 0.35 },
      { rarity: 'legendary', chance: 0.1 },
    ],
    failureHpLossMin: 50,
    failureHpLossMax: 80,
    captureDestination: 'jail',
    captureDurationMin: 60,
    captureDurationMax: 120,
    cooldownDays: 0.5,
    refreshCostMarks: 50,
    flavor:
      "A merchant's private vault. Hidden in the cellar. Guarded by trapdoors, false floors, and a clockwork combination only he knows. Time spent studying the layout pays off. So does luck.",
    successText:
      'The combination is three turns right, two left, one right. The door swings open onto neatly stacked silver ingots. You fill the sack and leave the way you came — through the coal chute.',
    unsuccessfulText:
      'The cellar entrance is bricked over since your last reconnaissance. New construction. The vault is inaccessible tonight.',
    failureText:
      'A false floor gives way under your weight. You fall eight feet onto flagstone and something in your ankle makes a sound it should not.',
    capturedText:
      'The combination was changed. As you work the dials, a steel portcullis drops behind you. The merchant descends the stairs at his leisure.',
  },
  {
    id: 'ghost_in_building',
    name: 'The Ghost in the Building',
    category: 'sneak',
    tier: 8,
    nerveCost: 15,
    levelRequired: 55,
    crimeSkillRequired: 75,
    baseOutcomes: { success: 0.5, unsuccessful: 0.23, failure: 0.22, captured: 0.05 },
    goldMin: 2000,
    goldMax: 6000,
    xpReward: 800,
    lootTable: [
      { rarity: 'epic', chance: 0.4 },
      { rarity: 'legendary', chance: 0.15 },
    ],
    failureHpLossMin: 60,
    failureHpLossMax: 100,
    captureDestination: 'jail',
    captureDurationMin: 120,
    captureDurationMax: 180,
    cooldownDays: 1,
    refreshCostMarks: 100,
    flavor:
      'Slip past patrols you cannot see. Climb walls you cannot grip. Open doors that should be locked. The best thieves leave nothing but absence: missing coin, missing jewels, missing only the awareness they were there.',
    successText:
      'You move through the building like smoke through a keyhole. Three floors, twelve rooms, not a single alarm. The haul fills two sacks and not a soul knows you were here.',
    unsuccessfulText:
      'The building has been reinforced since your last survey. New locks, new bars, a guard dog you were not told about. You withdraw and plan again.',
    failureText:
      'A tripwire at ankle height catches you on the third floor. The crash brings lanterns from every direction. You dive through a window and fall two stories into a market stall.',
    capturedText:
      'The building was a trap from the beginning. The valuables were paste. The guards were real. They close the net with military precision.',
  },
  {
    id: 'steal_letter_of_marque',
    name: 'Steal a Letter of Marque',
    category: 'sneak',
    tier: 9,
    nerveCost: 16,
    levelRequired: 75,
    crimeSkillRequired: 85,
    baseOutcomes: { success: 0.45, unsuccessful: 0.28, failure: 0.23, captured: 0.04 },
    goldMin: 5000,
    goldMax: 15000,
    xpReward: 1800,
    lootTable: [
      { rarity: 'epic', chance: 0.5 },
      { rarity: 'legendary', chance: 0.25 },
    ],
    failureHpLossMin: 80,
    failureHpLossMax: 130,
    captureDestination: 'jail',
    captureDurationMin: 240,
    captureDurationMax: 360,
    cooldownDays: 1,
    refreshCostMarks: 100,
    flavor:
      "The Crown's official document granting privateer status. Worth a fortune to forgers, more to actual pirates pretending legitimacy. Stored in only a handful of admiralty offices. Heavily guarded. Worth the risk if you can pull it off.",
    successText:
      "The admiralty seal cracks under your fingers as you slide the document from its case. Genuine parchment, genuine signature, genuine power. You're a legitimate privateer now — or at least you have the paper that says so.",
    unsuccessfulText:
      'The admiral is entertaining tonight. Every window in the office blazes with light and laughter. You watch from the garden wall and withdraw.',
    failureText:
      "A marine guard rounds the corner a full minute ahead of schedule. His bayonet opens your side before you've cleared the fence. You leave blood but no evidence.",
    capturedText:
      "The office safe opens onto a second safe, and the second onto an alarm bell. The admiral's personal guard arrives before you've processed your surprise.",
  },
  {
    id: 'master_burglar',
    name: 'The Master Burglar',
    category: 'sneak',
    tier: 10,
    nerveCost: 18,
    levelRequired: 100,
    crimeSkillRequired: 95,
    baseOutcomes: { success: 0.5, unsuccessful: 0.25, failure: 0.23, captured: 0.02 },
    goldMin: 10000,
    goldMax: 40000,
    xpReward: 3500,
    lootTable: [
      { rarity: 'legendary', chance: 0.5 },
      { rarity: 'epic', chance: 0.3 },
    ],
    failureHpLossMin: 100,
    failureHpLossMax: 180,
    captureDestination: 'jail',
    captureDurationMin: 480,
    captureDurationMax: 720,
    cooldownDays: 3,
    refreshCostMarks: 200,
    flavor:
      'A heist whispered about in three languages. A vault containing not gold but secrets. The fortune of an entire colony, sealed away by a paranoid governor. Few have tried. None have succeeded. Yet.',
    successText:
      'Twelve locks. Three blind corridors. One heartbeat at a time. The vault opens onto documents that will reshape the balance of power in the Caribbean. You take them all.',
    unsuccessfulText:
      "Intelligence suggests the governor has moved the vault's contents to a ship in the harbor. The building is empty. Another time.",
    failureText:
      'A pressure plate you missed. The ceiling drops iron bars across every exit. You batter through a wall with a stolen musket butt, bleeding from the ears from the alarm bells.',
    capturedText:
      "The governor himself is waiting in the vault, seated in a chair, reading by candlelight. He doesn't look up. His guards do the rest.",
  },
  {
    id: 'phantom_heist',
    name: 'Phantom Heist',
    category: 'sneak',
    tier: 10,
    nerveCost: 18,
    levelRequired: 125,
    crimeSkillRequired: 95,
    baseOutcomes: { success: 0.55, unsuccessful: 0.2, failure: 0.23, captured: 0.02 },
    goldMin: 20000,
    goldMax: 80000,
    xpReward: 7000,
    lootTable: [
      { rarity: 'legendary', chance: 0.6 },
      { rarity: 'epic', chance: 0.3 },
    ],
    failureHpLossMin: 120,
    failureHpLossMax: 200,
    captureDestination: 'jail',
    captureDurationMin: 720,
    captureDurationMax: 1440,
    cooldownDays: 7,
    refreshCostMarks: 350,
    flavor:
      "A heist so perfect it becomes its own legend. Steal something the world doesn't know exists. Leave behind no trace. The mark won't know what they lost until weeks after. By then you're three ports away.",
    successText:
      "In and out like a thought. The safe is empty, the guards undisturbed, the morning inventory unchanged. They won't discover the loss for a month. By then the contents will have been sold, resold, and vanished into the Caribbean wind.",
    unsuccessfulText:
      'The approach is compromised. A new garrison has been posted along the only viable route. Patience. The phantom waits for the next window.',
    failureText:
      'A single misplaced boot on a rain-slicked ledge. The fall is twenty feet. The pain is immediate and the retreat is slow, dragging one leg behind you through the dark.',
    capturedText:
      "Perfect execution, flawless entry, silent extraction — and a locked door that wasn't locked before. The building has sealed itself around you like a closing fist.",
  },

  // ===================================================================
  // SEA CRIMES — Tier 3-10
  // ===================================================================
  {
    id: 'smuggle_goods',
    name: 'Smuggle Goods Along the Coast',
    category: 'sea',
    tier: 3,
    nerveCost: 6,
    levelRequired: 5,
    crimeSkillRequired: 10,
    baseOutcomes: { success: 0.5, unsuccessful: 0.28, failure: 0.17, captured: 0.05 },
    goldMin: 40,
    goldMax: 150,
    xpReward: 65,
    lootTable: [{ rarity: 'uncommon', chance: 0.15 }],
    failureHpLossMin: 0,
    failureHpLossMax: 0,
    captureDestination: 'jail',
    captureDurationMin: 20,
    captureDurationMax: 20,
    requiresShip: true,
    requiredShipClass: 'sloop',
    flavor:
      'Run goods between ports without paying the harbor tax. Stay off the main lanes. Avoid the navy. Make port at night. Profitable for those with the nerve.',
    successText:
      'The cargo slides ashore in the dark. The harbormaster sleeps through it. By dawn your hold is empty and your purse is full.',
    unsuccessfulText:
      'A naval cutter patrols the cove mouth all night. You circle twice, decide against it, and sail home with the cargo still aboard.',
    failureText:
      'A squall catches you broadside near the point. The cargo shifts and your hull scrapes a reef. You limp to port with water in the bilge and nothing to show for it.',
    capturedText:
      "The harbor patrol spots your lantern. They're alongside before you can douse it. The cargo is seized and you're in irons by first light.",
  },
  {
    id: 'hunt_lone_merchant',
    name: 'Hunt a Lone Merchant',
    category: 'sea',
    tier: 4,
    nerveCost: 8,
    levelRequired: 12,
    crimeSkillRequired: 20,
    baseOutcomes: { success: 0.5, unsuccessful: 0.27, failure: 0.18, captured: 0.05 },
    goldMin: 150,
    goldMax: 450,
    xpReward: 110,
    lootTable: [
      { rarity: 'uncommon', chance: 0.25 },
      { rarity: 'rare', chance: 0.08 },
    ],
    failureHpLossMin: 0,
    failureHpLossMax: 0,
    captureDestination: 'jail',
    captureDurationMin: 30,
    captureDurationMax: 30,
    requiresShip: true,
    requiredShipClass: 'sloop',
    flavor:
      'A merchant ship sails alone, away from the main lanes. Foolish but profitable. Catch it before sundown. Board it before the navy hears the cannon fire.',
    successText:
      'Two warning shots and the merchant strikes her colors. Her hold is full of sugar and rum. Your crew cheers as the cargo swings across on ropes.',
    unsuccessfulText:
      "The merchant sees you coming and runs before the wind. She's faster than she looks. By sunset she's a speck on the horizon.",
    failureText:
      'The merchant fights back. A lucky shot from her stern chaser splinters your bowsprit. You break off the pursuit to save your rigging.',
    capturedText:
      "The merchant was flying false colors. She's an armed brig with twelve guns per side. Her broadside rakes your deck and you strike before the second volley.",
  },
  {
    id: 'raid_fishing_village',
    name: 'Raid a Coastal Fishing Village',
    category: 'sea',
    tier: 5,
    nerveCost: 9,
    levelRequired: 15,
    crimeSkillRequired: 30,
    baseOutcomes: { success: 0.5, unsuccessful: 0.25, failure: 0.2, captured: 0.05 },
    goldMin: 200,
    goldMax: 600,
    xpReward: 130,
    lootTable: [
      { rarity: 'uncommon', chance: 0.3 },
      { rarity: 'rare', chance: 0.1 },
    ],
    failureHpLossMin: 0,
    failureHpLossMax: 0,
    captureDestination: 'hospital',
    captureDurationMin: 30,
    captureDurationMax: 30,
    requiresShip: true,
    requiredShipClass: 'brigantine',
    flavor:
      "A small fishing village. No defenses worth mentioning. The men are out with the fleet. Take what's there. Leave nothing valuable behind. Be at sea by sunrise.",
    successText:
      "The village yields salted fish, copper pots, and a locked strongbox from the headman's house. Your crew loads the boats in silence. You're at sea before the fishermen return.",
    unsuccessfulText:
      'Smoke rises from the village — a Spanish patrol boat is beached in the shallows. The village is protected today. You sail past without stopping.',
    failureText:
      "The headman's wife raises the alarm with a conch shell. Boats appear from the cove. Your crew takes fire from muskets on the beach and you withdraw with damaged rigging.",
    capturedText:
      'The fishing fleet returns early. Twelve boats surround your vessel in the shallows. Your crew is outnumbered and the bosun takes a harpoon through his shoulder before you can surrender.',
  },
  {
    id: 'raid_spanish_patrol',
    name: 'Raid a Spanish Patrol',
    category: 'sea',
    tier: 6,
    nerveCost: 11,
    levelRequired: 25,
    crimeSkillRequired: 45,
    baseOutcomes: { success: 0.45, unsuccessful: 0.3, failure: 0.2, captured: 0.05 },
    goldMin: 600,
    goldMax: 1800,
    xpReward: 280,
    lootTable: [
      { rarity: 'rare', chance: 0.25 },
      { rarity: 'epic', chance: 0.1 },
    ],
    failureHpLossMin: 0,
    failureHpLossMax: 0,
    captureDestination: 'jail',
    captureDurationMin: 60,
    captureDurationMax: 60,
    requiresShip: true,
    requiredShipClass: 'frigate',
    flavor:
      'Spanish patrol galleon. Two cannon batteries. Forty marines. You have firepower and surprise. They have armor and numbers. The cargo, if you can take it, includes silver bound for Madrid.',
    successText:
      "Your broadside catches her amidships at dawn. Her mainmast goes by the board. By noon you're transferring chests of silver while the Spanish captain watches from his shattered quarterdeck.",
    unsuccessfulText:
      'The patrol sails in convoy today — three warships in line abreast. Even you know when to count the guns and sail the other way.',
    failureText:
      "She's faster to her guns than expected. Her first broadside takes out your foremast and you spend the day limping to a friendly port, cursing the Spanish in two languages.",
    capturedText:
      'The Spanish captain is a veteran. He crosses your bow and rakes you stem to stern. Your crew is on its knees before the boarding party reaches your deck.',
  },
  {
    id: 'treasure_diving',
    name: 'Treasure Diving',
    category: 'sea',
    tier: 6,
    nerveCost: 12,
    levelRequired: 28,
    crimeSkillRequired: 45,
    baseOutcomes: { success: 0.5, unsuccessful: 0.25, failure: 0.2, captured: 0.05 },
    goldMin: 500,
    goldMax: 1500,
    xpReward: 250,
    lootTable: [
      { rarity: 'rare', chance: 0.4 },
      { rarity: 'epic', chance: 0.08 },
    ],
    failureHpLossMin: 30,
    failureHpLossMax: 50,
    captureDestination: 'hospital',
    captureDurationMin: 30,
    captureDurationMax: 30,
    requiresShip: true,
    requiredShipClass: 'sloop',
    cooldownDays: 0.167,
    refreshCostMarks: 20,
    flavor:
      'An old wreck sits in shallow water near a hidden cove. The bones of a Spanish galleon, mast still showing at low tide. Hold your breath. Dive deep. Bring something back.',
    successText:
      'Three dives. On the last, your hand finds a chest wedged under a collapsed beam. The lock is rusted through. Inside: gold escudos, barnacle-crusted but genuine.',
    unsuccessfulText:
      'The current is too strong today. You make two dives but the wreck shifts with the tide and the entry is blocked. You surface empty-handed.',
    failureText:
      'A beam shifts as you squeeze through a gap in the hull. Your arm is pinned for thirty seconds of blind panic before you wrench free. You surface gasping with a gash from elbow to wrist.',
    capturedText:
      'The wreck is deeper than you thought. Your lungs scream. Your vision narrows. Your crew pulls you aboard more dead than alive, retching seawater.',
  },
  {
    id: 'hunt_royal_mail',
    name: 'Hunt the Royal Mail Packet',
    category: 'sea',
    tier: 7,
    nerveCost: 14,
    levelRequired: 40,
    crimeSkillRequired: 60,
    baseOutcomes: { success: 0.45, unsuccessful: 0.28, failure: 0.22, captured: 0.05 },
    goldMin: 1500,
    goldMax: 4500,
    xpReward: 550,
    lootTable: [
      { rarity: 'epic', chance: 0.35 },
      { rarity: 'legendary', chance: 0.12 },
    ],
    failureHpLossMin: 0,
    failureHpLossMax: 0,
    captureDestination: 'hospital',
    captureDurationMin: 120,
    captureDurationMax: 120,
    requiresShip: true,
    requiredShipClass: 'frigate',
    cooldownDays: 0.25,
    refreshCostMarks: 30,
    flavor:
      'The Royal Mail Packet runs between Caribbean colonies. Diplomatic correspondence. Crown gold. Sometimes prisoners. Always escorted. Always lucrative. Always remembered if you take it.',
    successText:
      'The escort peels off when your guns speak. The packet strikes her colors after a single broadside. Crown gold, sealed dispatches, and a hold full of mail that will never reach London.',
    unsuccessfulText:
      'The packet sails under escort of a forty-gun frigate today. The Crown is taking no chances. You shadow them for an hour, then turn for open water.',
    failureText:
      'The escort frigate is faster than expected. Her bowchasers find your stern and a lucky shot takes your rudder. You limp away, steering with sweeps, while the packet sails on untouched.',
    capturedText:
      'It was an ambush. Two frigates emerge from behind the headland. The packet was bait. Your crew fights hard but the numbers are overwhelming.',
  },
  {
    id: 'plunder_galleon',
    name: 'Plunder a Galleon',
    category: 'sea',
    tier: 8,
    nerveCost: 16,
    levelRequired: 55,
    crimeSkillRequired: 75,
    baseOutcomes: { success: 0.45, unsuccessful: 0.25, failure: 0.25, captured: 0.05 },
    goldMin: 3000,
    goldMax: 10000,
    xpReward: 1000,
    lootTable: [
      { rarity: 'epic', chance: 0.45 },
      { rarity: 'legendary', chance: 0.15 },
    ],
    failureHpLossMin: 0,
    failureHpLossMax: 0,
    captureDestination: 'hospital',
    captureDurationMin: 180,
    captureDurationMax: 240,
    requiresShip: true,
    requiredShipClass: 'frigate',
    cooldownDays: 0.5,
    refreshCostMarks: 50,
    flavor:
      'A Spanish treasure galleon, lumbering home with two years of silver mined from the New World. The crew is exhausted. The captain is desperate. You are hungry. The fight is brutal. The reward is generational.',
    successText:
      "Four hours of broadside-to-broadside battle. Her mainmast falls at noon. By evening you're aboard, your boots ankle-deep in silver bars. The crew will eat like kings for a year.",
    unsuccessfulText:
      'The galleon sails in the center of a convoy of six. You count the gun ports and make the only sane decision. Live to fight another day.',
    failureText:
      'Her guns are better manned than you expected. A twelve-pound ball punches through your hull below the waterline. The crew spends the night pumping while the galleon sails away.',
    capturedText:
      'The galleon was carrying more than silver — she had a company of marines below decks. They swarm your ship like ants. The fight is short, bloody, and decisive.',
  },
  {
    id: 'hunt_the_kraken',
    name: 'Hunt the Kraken',
    category: 'sea',
    tier: 9,
    nerveCost: 17,
    levelRequired: 75,
    crimeSkillRequired: 85,
    baseOutcomes: { success: 0.5, unsuccessful: 0.25, failure: 0.23, captured: 0.02 },
    goldMin: 8000,
    goldMax: 25000,
    xpReward: 2500,
    lootTable: [
      { rarity: 'epic', chance: 0.6 },
      { rarity: 'legendary', chance: 0.2 },
    ],
    failureHpLossMin: 0,
    failureHpLossMax: 0,
    captureDestination: 'hospital',
    captureDurationMin: 360,
    captureDurationMax: 360,
    requiresShip: true,
    requiredShipClass: 'galleon',
    cooldownDays: 1,
    refreshCostMarks: 100,
    flavor:
      'Some say it lives in the deepest trench. Others say it walks the ocean floor. All agree: when the kraken rises, ships sink. The tentacles wrap around the mast. The eye watches. The hunt is on.',
    successText:
      'The beast surfaces at dusk. Tentacles as thick as masts thrash the water white. Your gunners fire until the barrels glow. It submerges at last, trailing black blood. The teeth it shed are worth more than gold.',
    unsuccessfulText:
      'The sea is dead calm for three days. The bait drifts untouched. Whatever lives below has no interest in you today.',
    failureText:
      'A tentacle wraps your mainmast and snaps it like kindling. The ship heels forty degrees. Your crew cuts the rigging free while the beast drags timber into the deep. You limp home missing a mast and seven men.',
    capturedText:
      'It rises beneath you. The hull groans. The deck splits. Water pours through the seams faster than any pump can handle. Your crew abandons ship in the longboats and watches your vessel go down stern-first into the dark.',
  },
  {
    id: 'ghost_ship_encounter',
    name: 'The Ghost Ship Encounter',
    category: 'sea',
    tier: 9,
    nerveCost: 16,
    levelRequired: 80,
    crimeSkillRequired: 85,
    baseOutcomes: { success: 0.5, unsuccessful: 0.25, failure: 0.23, captured: 0.02 },
    goldMin: 5000,
    goldMax: 20000,
    xpReward: 2300,
    lootTable: [
      { rarity: 'epic', chance: 0.5 },
      { rarity: 'legendary', chance: 0.25 },
    ],
    failureHpLossMin: 80,
    failureHpLossMax: 120,
    captureDestination: 'hospital',
    captureDurationMin: 240,
    captureDurationMax: 240,
    requiresShip: true,
    requiredShipClass: 'sloop',
    cooldownDays: 1,
    refreshCostMarks: 100,
    flavor:
      'The Flying Dutchman. The Mary Celeste. The Black Damask. Ships that should not sail. Crews that should not man them. They appear in the fog and vanish in the storm. What they carry, they took from the living.',
    successText:
      "You board her in the fog. The crew stands motionless, watching with eyes that don't blink. The hold is full of treasure from a century of ships that never made port. You take what you can carry and don't look back.",
    unsuccessfulText:
      'The fog rolls in but the ship never appears. Your crew watches the mist all night, hearing things that might be rigging, or might be voices. Dawn breaks with nothing to show.',
    failureText:
      'She appears and disappears between waves. Your crew fires into the fog. Something fires back — not cannons, but a cold wind that freezes the breath in your lungs and puts three men on their backs, screaming at nothing.',
    capturedText:
      "You board her. The hold is empty. The deck is empty. Your crew begins to scream. When you come to, you're back on your own ship, drifting. Three days have passed. No one remembers what happened.",
  },
  {
    id: 'davy_jones_domain',
    name: "Davy Jones's Domain",
    category: 'sea',
    tier: 10,
    nerveCost: 18,
    levelRequired: 100,
    crimeSkillRequired: 95,
    baseOutcomes: { success: 0.45, unsuccessful: 0.25, failure: 0.27, captured: 0.03 },
    goldMin: 20000,
    goldMax: 60000,
    xpReward: 6000,
    lootTable: [
      { rarity: 'legendary', chance: 0.7 },
      { rarity: 'epic', chance: 0.2 },
    ],
    failureHpLossMin: 0,
    failureHpLossMax: 0,
    captureDestination: 'hospital',
    captureDurationMin: 720,
    captureDurationMax: 720,
    requiresShip: true,
    requiredShipClass: 'galleon',
    cooldownDays: 3,
    refreshCostMarks: 200,
    flavor:
      'The deepest part of the Caribbean. Where the sea floor meets nothing. Sailors who venture there return changed, or not at all. Davy Jones himself walks the deck at night. Speak no oath you cannot keep. Bring no greed you cannot defend.',
    successText:
      'The deep gives up its dead. Chests from a hundred sunken ships rise with the current — gold, jewels, instruments of navigation that predate the living. Your crew works in terrified silence. You leave before the water changes color.',
    unsuccessfulText:
      'The sea is black and still. Your depth line finds no bottom. Your crew refuses to lower the boats. Superstition wins tonight. You sail for shallower water.',
    failureText:
      'The water turns cold. The compass spins. Something scrapes along the hull from bow to stern — slowly, deliberately, like a finger tracing a line. Three planks crack and the sea pours in. Your crew pumps for eighteen hours straight.',
    capturedText:
      'The ship descends. Not sinking — descending. The water rises past the rail without waves, without wind. Your crew screams. The surgeon goes mad. When the ship surfaces three days later, you are fewer, older, and silent.',
  },
  {
    id: 'the_last_crusade',
    name: 'The Last Crusade',
    category: 'sea',
    tier: 10,
    nerveCost: 18,
    levelRequired: 150,
    crimeSkillRequired: 95,
    baseOutcomes: { success: 0.5, unsuccessful: 0.23, failure: 0.25, captured: 0.02 },
    goldMin: 50000,
    goldMax: 200000,
    xpReward: 12000,
    lootTable: [
      { rarity: 'legendary', chance: 0.8 },
      { rarity: 'epic', chance: 0.15 },
    ],
    failureHpLossMin: 0,
    failureHpLossMax: 0,
    captureDestination: 'hospital',
    captureDurationMin: 1440,
    captureDurationMax: 1440,
    requiresShip: true,
    requiredShipClass: 'galleon',
    cooldownDays: 7,
    refreshCostMarks: 350,
    flavor:
      'A mythical Spanish galleon, said to have sunk with the spoils of an entire crusade aboard. Untouched for two centuries. Cursed by every prayer of its lost crew. The deepest treasure in the Caribbean. The most dangerous expedition known to pirates. Few attempt it. Fewer return.',
    successText:
      'She sits upright on the sea floor, masts intact, as if she sank yesterday. The hold is a cathedral of gold — crucifixes, chalices, bars stamped with the seal of Castile. You surface with enough wealth to buy a port.',
    unsuccessfulText:
      'The currents are wrong. The dive site has shifted. Your navigator insists the coordinates are correct but the sea disagrees. You circle for two days and find nothing.',
    failureText:
      'The wreck is there. The curse is real. Timbers shift as your divers enter the hold. The ship groans, rolls, and settles deeper. Two men are trapped below. You save one.',
    capturedText:
      "The crusade's dead do not rest. The water around the wreck turns black. Your rigging freezes. Your crew cannot speak, cannot move, cannot look away from the shapes rising from the deep. You lose the ship, the crew, and something harder to name.",
  },

  // ===================================================================
  // HEIST CRIMES — Tier 8-10
  // ===================================================================
  {
    id: 'bank_robbery',
    name: 'Bank Robbery',
    category: 'heist',
    tier: 8,
    nerveCost: 15,
    levelRequired: 50,
    crimeSkillRequired: 75,
    baseOutcomes: { success: 0.45, unsuccessful: 0.28, failure: 0.22, captured: 0.05 },
    goldMin: 2000,
    goldMax: 6000,
    xpReward: 900,
    lootTable: [
      { rarity: 'epic', chance: 0.4 },
      { rarity: 'legendary', chance: 0.15 },
    ],
    failureHpLossMin: 70,
    failureHpLossMax: 110,
    captureDestination: 'jail',
    captureDurationMin: 120,
    captureDurationMax: 180,
    cooldownDays: 0.25,
    refreshCostMarks: 30,
    flavor:
      'The Port Royale Mercantile Bank. Thick walls. Vault doors three feet thick. A vault clerk who claims he was hired for his integrity. Tonight, you find out how integral his integrity actually is.',
    successText:
      "The clerk's integrity costs exactly forty doubloons. He opens the vault, looks the other way, and closes it again after you've filled three sacks. Professional courtesy.",
    unsuccessfulText:
      'The bank is hosting its quarterly audit tonight. Every lamp is lit and every clerk is present. Even the vault is open, watched by four armed men. Not tonight.',
    failureText:
      "The vault combination has been changed. You're still working the dial when the night watchman rounds the corner. His club finds the back of your head and everything goes dark.",
    capturedText:
      "The clerk was a plant. The vault was empty. The garrison was in the basement. You're in manacles before you've finished expressing your disappointment.",
  },
  {
    id: 'governors_mansion',
    name: "Governor's Mansion",
    category: 'heist',
    tier: 8,
    nerveCost: 16,
    levelRequired: 60,
    crimeSkillRequired: 75,
    baseOutcomes: { success: 0.45, unsuccessful: 0.27, failure: 0.22, captured: 0.06 },
    goldMin: 3000,
    goldMax: 9000,
    xpReward: 1200,
    lootTable: [
      { rarity: 'epic', chance: 0.5 },
      { rarity: 'legendary', chance: 0.18 },
    ],
    failureHpLossMin: 80,
    failureHpLossMax: 120,
    captureDestination: 'jail',
    captureDurationMin: 180,
    captureDurationMax: 240,
    cooldownDays: 0.5,
    refreshCostMarks: 50,
    flavor:
      "Spanish Governor's mansion in Cartagena. Personal fortune locked behind reinforced doors. His mistress sleeps lightly. His guards drink heavily. Pick your moment carefully. The cost of failure is not just imprisonment.",
    successText:
      "The guards are insensible with rum. The mistress sleeps through the click of the lock. The governor's private study yields a chest of emeralds and a pouch of gold reales. You're over the garden wall before the candle burns down.",
    unsuccessfulText:
      'The governor is hosting a dinner party. Carriages line the drive and laughter spills from every window. The house is alive tonight. You watch from the trees and withdraw.',
    failureText:
      "The mistress is not asleep. She screams. The guards may be drunk but they're still armed. A musket ball punches through the shutters as you vault the balcony railing.",
    capturedText:
      "The governor was expecting a thief — just not tonight. His personal bodyguard catches you in the study with your hands in the emerald chest. He's quiet, efficient, and significantly larger than you.",
  },
  {
    id: 'fort_heist',
    name: 'Fort Heist',
    category: 'heist',
    tier: 9,
    nerveCost: 17,
    levelRequired: 70,
    crimeSkillRequired: 85,
    baseOutcomes: { success: 0.45, unsuccessful: 0.28, failure: 0.23, captured: 0.04 },
    goldMin: 5000,
    goldMax: 15000,
    xpReward: 1800,
    lootTable: [
      { rarity: 'epic', chance: 0.45 },
      { rarity: 'legendary', chance: 0.22 },
    ],
    failureHpLossMin: 100,
    failureHpLossMax: 150,
    captureDestination: 'hospital',
    captureDurationMin: 300,
    captureDurationMax: 360,
    requiredLocation: 'charleston',
    cooldownDays: 1,
    refreshCostMarks: 100,
    flavor:
      "Fort Charles. Built to hold a city's gold. Garrison of fifty men. Walls forty feet thick. The treasury is on the third floor, accessed only through the commandant's office, which is always locked. Almost always. Tonight, perhaps not.",
    successText:
      "The commandant dines at the Governor's residence every Thursday. Tonight is Thursday. His office door yields to a pick and a prayer. The treasury beyond holds enough Crown gold to fund a small war.",
    unsuccessfulText:
      'The garrison is on alert — a ship was sighted flying questionable colors. Every lamp is lit and every musket loaded. The fort is impregnable tonight.',
    failureText:
      'You make it to the third floor before a loose flagstone alerts a sentry. His bayonet opens your arm and the alarm bell begins to ring. You escape through a drainage tunnel, bleeding heavily.',
    capturedText:
      "The commandant wasn't at dinner. He was in his office, loading his pistol. The door opens both ways, and tonight it opens onto your capture.",
  },
  {
    id: 'royal_galleon_heist',
    name: 'Royal Galleon Heist',
    category: 'heist',
    tier: 9,
    nerveCost: 18,
    levelRequired: 85,
    crimeSkillRequired: 85,
    baseOutcomes: { success: 0.5, unsuccessful: 0.25, failure: 0.22, captured: 0.03 },
    goldMin: 10000,
    goldMax: 30000,
    xpReward: 3500,
    lootTable: [
      { rarity: 'legendary', chance: 0.55 },
      { rarity: 'epic', chance: 0.3 },
    ],
    failureHpLossMin: 80,
    failureHpLossMax: 130,
    captureDestination: 'hospital',
    captureDurationMin: 360,
    captureDurationMax: 480,
    requiresShip: true,
    requiredShipClass: 'galleon',
    crossSkillRequirements: { crimeSkill: 70, seaCrimeSkill: 60, seamanship: 50 },
    cooldownDays: 1,
    refreshCostMarks: 100,
    flavor:
      "The Spanish flagship of the Caribbean fleet. Personal vessel of an admiral. Carries the year's tribute to Madrid. Heavily armed, heavily crewed, heavily defended. The reward, if you can take her, is generations of wealth. The cost of failure is a fate worse than death.",
    successText:
      'Night boarding. Your crew swarms the deck in silence. The admiral is bound before he wakes. The tribute — six chests of silver, two of gold, and a crown jewel collection — transfers to your ship by dawn.',
    unsuccessfulText:
      'The flagship sails with a full battle escort. Three frigates, two corvettes, and a sloop screen every approach. The tribute will reach Madrid this year.',
    failureText:
      'The boarding goes wrong. A marine sergeant rallies his men and your crew is pushed back to the rails. You lose eleven men retreating to your own ship under musket fire.',
    capturedText:
      'The admiral was awake. The marines were waiting below decks. Your boarding party is cut to pieces in the narrow companionways. The survivors, including you, are chained in the orlop.',
  },
  {
    id: 'vault_of_cortez',
    name: 'The Vault of Cortez',
    category: 'heist',
    tier: 10,
    nerveCost: 18,
    levelRequired: 100,
    crimeSkillRequired: 95,
    baseOutcomes: { success: 0.45, unsuccessful: 0.27, failure: 0.25, captured: 0.03 },
    goldMin: 30000,
    goldMax: 100000,
    xpReward: 6500,
    lootTable: [
      { rarity: 'legendary', chance: 0.7 },
      { rarity: 'epic', chance: 0.2 },
    ],
    failureHpLossMin: 120,
    failureHpLossMax: 180,
    captureDestination: 'jail',
    captureDurationMin: 720,
    captureDurationMax: 720,
    cooldownDays: 3,
    refreshCostMarks: 200,
    flavor:
      "Conquistador's gold, hidden in a chamber sealed for two centuries. The location was passed down in fragments. The traps are still active. The curse is still real. Many have died trying. The treasure that exists, exists.",
    successText:
      'The traps are old but you are older in patience. Each one yields to careful study. The vault opens onto gold that has not seen light since Cortez himself sealed the door. Aztec masks. Emerald idols. More wealth than any living pirate has held.',
    unsuccessfulText:
      'The map fragment leads to a collapsed tunnel. The entrance was sealed by an earthquake decades ago. The vault of Cortez keeps its secrets a while longer.',
    failureText:
      "A trap you didn't see. Darts from the walls, tipped with something that burns. Your vision blurs and your hands shake. You retreat through corridors that seem to shift and breathe around you. The curse is not a legend.",
    capturedText:
      'You reach the vault. It is open. It is empty. And the walls are closing. When they find you, days later, you are alive but changed. The Spaniards who imprisoned you say you spoke a language no one recognized.',
  },
  {
    id: 'kings_privateer_charter',
    name: "The King's Privateer Charter",
    category: 'heist',
    tier: 10,
    nerveCost: 18,
    levelRequired: 120,
    crimeSkillRequired: 95,
    baseOutcomes: { success: 0.45, unsuccessful: 0.25, failure: 0.27, captured: 0.03 },
    goldMin: 50000,
    goldMax: 150000,
    xpReward: 9000,
    lootTable: [
      { rarity: 'legendary', chance: 0.8 },
      { rarity: 'epic', chance: 0.1 },
    ],
    failureHpLossMin: 150,
    failureHpLossMax: 200,
    captureDestination: 'jail',
    captureDurationMin: 1080,
    captureDurationMax: 1440,
    crossSkillRequirements: { crimeSkill: 90, seaCrimeSkill: 70 },
    cooldownDays: 7,
    refreshCostMarks: 350,
    flavor:
      "The original Charter of Privateering, signed by the King himself, kept in the highest court of the Crown. Real proof of legitimate piracy. Worth more than the gold in any one vault. Defended like the realm's last secret.",
    successText:
      "Three months of planning. One night of execution. The Charter passes from the Crown's hands to yours. Every port in the Caribbean will recognize this seal. You are, by royal decree, untouchable.",
    unsuccessfulText:
      'The Crown Court is hosting a state dinner. Every corridor blazes with light and every door is guarded by men who take their positions seriously. The Charter remains where it is.',
    failureText:
      "You are deep inside when the alarms sound. The retreat costs you blood, two good men, and the certainty that the Crown now knows your face. Every port will have your description by week's end.",
    capturedText:
      "The Crown's intelligence service was waiting. They knew the date, the route, and the team. You are taken alive — intentionally — and the interrogation begins before you've stopped bleeding. The Crown does not forget its enemies.",
  },
  {
    id: 'pirate_kings_vault',
    name: "The Pirate King's Vault",
    category: 'heist',
    tier: 10,
    nerveCost: 18,
    levelRequired: 140,
    crimeSkillRequired: 95,
    baseOutcomes: { success: 0.5, unsuccessful: 0.23, failure: 0.25, captured: 0.02 },
    goldMin: 80000,
    goldMax: 250000,
    xpReward: 14000,
    lootTable: [
      { rarity: 'legendary', chance: 0.85 },
      { rarity: 'epic', chance: 0.1 },
    ],
    failureHpLossMin: 180,
    failureHpLossMax: 250,
    captureDestination: 'jail',
    captureDurationMin: 1440,
    captureDurationMax: 2160,
    crossSkillRequirements: { crimeSkill: 95, seaCrimeSkill: 80, seamanship: 70 },
    cooldownDays: 7,
    refreshCostMarks: 350,
    flavor:
      "The legendary pirate Blackthorn's hidden treasure, said to contain the spoils of a thousand raids. The location is known. The defenses are not. Pirates have searched for it for two centuries. Some have come close. None have succeeded. Yet.",
    successText:
      "Blackthorn's vault is real. The defenses are mechanical, ingenious, and lethal — but not insurmountable. Inside: the accumulated plunder of the Caribbean's most successful pirate. Gold enough to buy a kingdom. And a crown to go with it.",
    unsuccessfulText:
      "The entrance is sealed by tides that won't recede for another month. The vault has waited two centuries. It can wait a little longer.",
    failureText:
      "Blackthorn's traps are as cunning dead as he was alive. A counterweight drops a portcullis that nearly takes your head. You crawl out through a drainage tunnel, broken and empty-handed. Every pirate between here and Nassau will hear you failed.",
    capturedText:
      'The vault is guarded by something that is not mechanical and is not alive. Whatever Blackthorn left behind to watch his gold does not negotiate, does not tire, and does not forgive. You are found a week later, wandering the beach, unable to speak of what you saw.',
  },
  {
    id: 'the_locker',
    name: 'The Locker',
    category: 'heist',
    tier: 10,
    nerveCost: 18,
    levelRequired: 175,
    crimeSkillRequired: 95,
    baseOutcomes: { success: 0.5, unsuccessful: 0.22, failure: 0.25, captured: 0.03 },
    goldMin: 200000,
    goldMax: 500000,
    xpReward: 30000,
    lootTable: [
      { rarity: 'legendary', chance: 0.95 },
      { rarity: 'epic', chance: 0.5 },
    ],
    failureHpLossMin: 200,
    failureHpLossMax: 300,
    captureDestination: 'hospital',
    captureDurationMin: 2880,
    captureDurationMax: 4320,
    requiresShip: true,
    requiredShipClass: 'galleon',
    crossSkillRequirements: { seaCrimeSkill: 99, seamanship: 99 },
    cooldownDays: 14,
    refreshCostMarks: 500,
    flavor:
      "Davy Jones's Locker itself. The bottom of the deep. Where ships go when they die and souls go when they sin. To enter is to challenge an old god. To return is to be remembered forever. To fail is to be the last bone in a thousand wrecks.",
    successText:
      'You descend. You see what lies at the bottom. You take what the Locker offers — treasure beyond counting, artifacts from ships that sailed before maps existed, gold coined in empires that no longer have names. You return. You are changed. You are Saltbound.',
    unsuccessfulText:
      'The sea refuses you. Three times you sail the coordinates. Three times the water is calm, clear, and empty. The Locker opens when it chooses, not when you choose.',
    failureText:
      'The descent begins but the Locker does not welcome you. The pressure cracks your hull. The darkness presses against your mind. You surface with half a crew, no cargo, and nightmares that will last the rest of your life.',
    capturedText:
      'You enter the Locker. The Locker does not let you leave. Your ship is found adrift, empty, sails set, wheel lashed. The log ends mid-sentence. Months later, you wash ashore. You do not remember how. You do not remember anything clearly. But your hands are full of gold.',
  },
];

export function getCrimeById(id: string): Crime | undefined {
  return CRIMES.find((c) => c.id === id);
}

export function getCrimesByCategory(category: CrimeCategory): Crime[] {
  return CRIMES.filter((c) => c.category === category);
}

export function calculateSuccessRate(baseSuccess: number, masteryLevel: number): number {
  const bonus = Math.min(masteryLevel * 0.005, 0.5);
  return Math.min(baseSuccess + bonus, 0.9);
}
